import { PageTable } from './../../../components-common/layout/views/PageTable';
import { layoutWorkspace } from './../../_common/layout.workspace';
import { ConfigSection } from '@components-fabric';
import { buildingParameterEntity } from './building-parameters.entity';
import { clone } from 'ramda';
import { PageFormToolbar } from '../../../components-common/layout/views/PageFormToolbar';
import { BuildingParameterForm } from '../../../components-domain/bim-domain/parameters/BuildingParameterForm';
import { getParameterValidationSchema } from '../../../components-domain/bim-domain/parameters/parameter.schema';
import { compDBAdditionalCheck } from '../../../components-domain/bim-domain/defaults';

/**
 * Configuration Object: formScreen
 *
 * This object defines the configuration for the building parameter form screen.
 * It specifies the component, form, template, validation schema, and initial empty values.
 *
 * @property {React.Component} Component - The component to render the form.
 * @property {React.Component} form - The form component used for creating and editing parameters.
 * @property {string} template - The template URL for the parameters.
 * @property {Object} schema - The validation schema for the form.
 * @property {Object} empty - The initial empty values for the form fields.
 */
const formScreen = {
  Component: PageFormToolbar,
  form: BuildingParameterForm,
  template: '/buildingms/categories/parameters',
  schema: getParameterValidationSchema(),
  empty: buildingParameterEntity.empty,
};

/**
 * Configuration Object: buildingParametersSection
 *
 * This object defines the configuration for the building parameters section.
 * It includes the section ID, title, layout, and an array of screen configurations.
 *
 * @property {string} id - The ID of the section.
 * @property {string} title - The title of the section.
 * @property {React.Component} layout - The layout component for the section.
 * @property {Array} screens - An array of screen configuration objects.
 */
export const buildingParametersSection: ConfigSection = {
  id: 'building_parameters',
  title: 'Building Parameters',
  layout: layoutWorkspace,
  screens: [
    {
      id: 'index',
      path: '.',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'index',
      columns: [
        'name',
        'type',
        { id: 'sourceType' },
        'min',
        'max',
        'value',
        'description',
      ],
      source: buildingParameterEntity.endpoints,
      Component: PageTable,
      urlParams: {
        category: 'BUILDING',
      },
      actions: [
        {
          id: 'edit',
          actionToDispatch: { payload: { path: '' } },
          additionalChecks: compDBAdditionalCheck,
          displayTooltip: true,
          tooltipText: 'Edit parameter',
        } as any,
        {
          id: 'copy',
          actionToDispatch: {
            payload: {
              copy: '/buildingms/categories/parameters/cloneParameter',
            },
          },
          displayTooltip: true,
          tooltipText: 'Duplicate parameter',
        } as any,
        {
          id: 'delete',
          actionToDispatch: {
            payload: {
              endpointToDelete: formScreen.template,
            },
          },
          additionalChecks: compDBAdditionalCheck,
          displayTooltip: true,
          tooltipText: 'Delete parameter',
        },
      ],
    } as any,
    {
      ...clone(formScreen),
      left: ['create'],
      title: 'Create Building Parameter',
      id: 'create',
      path: 'new',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'create',
    },
    {
      ...clone(formScreen),
      left: ['save'],
      title: 'Edit Building Parameter',
      right: ['back'],
      id: 'edit',
      path: ':buildingParameterId',
      editSource: `${formScreen.template}/`,
      idPropSource: 'buildingParameterId',
      roles: ['STUDY_DIRECTOR', 'LECTURER', 'STUDENT'],
      tmp: 'edit',
    },
  ],
};
